import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import CartDrawer from "@/components/CartDrawer";
import VoicesOfFaith from "@/components/VoicesOfFaith";
import { api, resolveAssetUrl } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";

const filters = [
  { key: "all", label: "All" },
  { key: "tops", label: "Tops" },
  { key: "compression", label: "Compression" },
  { key: "accessories", label: "Accessories" },
];

const Home = () => {
  const [products, setProducts] = useState([]);
  const [ratings, setRatings] = useState({});
  const [activeFilter, setActiveFilter] = useState("all");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);

  // Hero slideshow: cycle brand images every ~4 seconds
  const heroSlides = [
    BRAND.SPRINTERS,
    BRAND.POWERLIFTER,
    BRAND.STRENGTH_DUO,
    BRAND.FAMILY,
    BRAND.COLLECTION_SHEET,
  ];

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data || []))
      .catch((e) => console.error(e));
    api
      .get("/reviews/summary")
      .then((r) => setRatings(r.data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setHeroIdx((i) => (i + 1) % heroSlides.length);
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible =
    activeFilter === "all"
      ? products
      : products.filter((p) => p.category === activeFilter);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await api.post("/newsletter", { email });
      toast.success(res.data?.message || "Welcome to the legacy.");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Subscription failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overflow-x-hidden">
      <Header />
      <CartDrawer />

      {/* HERO */}
      <section
        className="relative min-h-[100vh] flex flex-col justify-end items-center text-center px-6 overflow-hidden bg-black"
        data-testid="hero-section"
      >
        {heroSlides.map((src, i) => (
          <div
            key={src}
            className={`hero-slide ${i === heroIdx ? "active" : ""}`}
            style={{ backgroundImage: `url(${resolveAssetUrl(src)})` }}
            aria-hidden="true"
          />
        ))}
        {/* Vignette: dark gradient on top + bottom so the headline reads cleanly
            no matter what's in the photo behind it */}
        <div
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(11,11,11,0.85) 0%, rgba(11,11,11,0.2) 30%, rgba(11,11,11,0.2) 65%, rgba(11,11,11,0.92) 100%)",
          }}
        ></div>

        <div className="relative z-10 max-w-4xl mx-auto space-y-5 pb-16 pt-12">
          <h2 className="text-sm md:text-base font-bold tracking-[0.4em] uppercase text-zinc-400 slide-in-left">
            EST. @2027
          </h2>
          <h1 className="font-heading text-5xl md:text-8xl font-black tracking-tight uppercase leading-[1.05] italic pr-2 hero-flash">
            <span className="slide-in-left inline-block">STRENGTH</span>
            <br />
            <span className="metallic-text slide-in-right inline-block">
              BUILT DIFFERENT
            </span>
          </h1>
          <p className="max-w-xl mx-auto text-sm md:text-base text-zinc-200 font-light tracking-wide drop-shadow-lg">
            Discipline. Sacrifice. Power. High-performance activewear engineered
            for those driven by an unstoppable future.
          </p>
          <div className="pt-4">
            <a
              href="#shop"
              className="bg-white text-black font-heading font-bold text-lg tracking-widest uppercase px-10 py-4 hover:bg-zinc-300 transition-all inline-block duration-300 transform hover:-translate-y-1"
              data-testid="hero-shop-cta"
            >
              Shop the Collection
            </a>
          </div>

          {/* Slide indicator dots */}
          <div className="flex justify-center gap-2 pt-6">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setHeroIdx(i)}
                aria-label={`Show slide ${i + 1}`}
                className={`w-8 h-1 transition-all ${
                  i === heroIdx ? "bg-white" : "bg-white/30 hover:bg-white/60"
                }`}
                data-testid={`hero-slide-dot-${i}`}
              />
            ))}
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full bg-zinc-950/80 border-t border-zinc-800 py-6 hidden md:block">
          <div className="max-w-7xl mx-auto grid grid-cols-3 text-center text-xs tracking-widest font-bold uppercase text-zinc-400">
            <div>
              <i className="fa-solid fa-cross mr-2 text-white"></i> FAITH FIRST
            </div>
            <div>
              <i className="fa-solid fa-shield-halved mr-2 text-white"></i>{" "}
              ELITE DISCIPLINE
            </div>
            <div>
              <i className="fa-solid fa-people-roof mr-2 text-white"></i> FAMILY
              LEGACY
            </div>
          </div>
        </div>
      </section>

      {/* SHOP */}
      <section
        id="shop"
        className="max-w-7xl mx-auto px-6 py-24 scroll-mt-24"
        data-testid="shop-section"
      >
        <div className="flex flex-col md:flex-row justify-between items-end mb-12">
          <div>
            <h3 className="text-xs font-bold tracking-[0.3em] text-zinc-400 uppercase mb-2">
              // HIGH-PERFORMANCE GEAR
            </h3>
            <h2 className="font-heading text-4xl font-bold uppercase tracking-tight">
              The Armor of Discipline
            </h2>
          </div>
          <div className="flex space-x-4 mt-4 md:mt-0 font-bold text-xs uppercase tracking-wider">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`pb-1 transition-colors cursor-pointer ${
                  activeFilter === f.key
                    ? "border-b-2 border-white text-white"
                    : "text-zinc-500 hover:text-white"
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
          data-testid="products-grid"
        >
          {visible.length === 0 ? (
            <div className="col-span-full text-center text-zinc-500 text-sm tracking-wide py-12">
              No gear in this category yet.
            </div>
          ) : (
            visible.map((p) => (
              <ProductCard key={p.id} product={p} rating={ratings[p.id]} />
            ))
          )}
        </div>
      </section>

      {/* MOVEMENT BANNER */}
      <section
        className="collection-banner-bg relative py-24 border-y border-zinc-900"
        style={{ backgroundImage: `url(${resolveAssetUrl(BRAND.COLLECTION_SHEET)})` }}
        data-testid="movement-banner"
      >
        <div className="relative z-10 max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="space-y-5">
            <div className="text-xs tracking-[0.4em] uppercase text-amber-400/80">
              Sisters · One Mission
            </div>
            <h2 className="font-heading text-5xl md:text-7xl font-black tracking-tight uppercase leading-[0.9]">
              We Train. <br />
              We Push. <br />
              <span className="metallic-text">We Build Legacy.</span>
            </h2>
            <p className="text-zinc-300 text-sm md:text-base max-w-md leading-relaxed">
              More than athletes — we are a movement. Speed, strength, and
              faith forged into every garment. Join the sisters and brothers
              suiting up for a higher purpose.
            </p>
            <div className="grid grid-cols-4 gap-4 pt-4 text-[10px] tracking-[0.3em] uppercase text-zinc-400">
              <div>
                <div className="text-white text-sm tracking-widest">Train</div>
                Together
              </div>
              <div>
                <div className="text-white text-sm tracking-widest">Push</div>
                Together
              </div>
              <div>
                <div className="text-white text-sm tracking-widest">Win</div>
                Together
              </div>
              <div>
                <div className="text-white text-sm tracking-widest">Build</div>
                Together
              </div>
            </div>
            <div className="pt-4">
              <a
                href="#shop"
                className="inline-block border-2 border-white text-white font-heading font-bold text-sm tracking-widest uppercase px-8 py-3 hover:bg-white hover:text-black transition-colors"
                data-testid="movement-cta"
              >
                Join the Movement
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-br from-amber-500/20 to-transparent blur-xl"></div>
            <img
              src={BRAND.COLLECTION_SHEET}
              alt="Faith Athletic full collection"
              className="relative w-full border border-zinc-800 shadow-2xl"
              data-testid="movement-collection-img"
            />
          </div>
        </div>
      </section>

      {/* ATHLETE TESTIMONIAL */}
      <VoicesOfFaith />

      {/* LADY STARZZ TEASER */}
      <section
        className="relative overflow-hidden bg-[#0b0b0b] border-y border-zinc-900"
        data-testid="lady-starzz-teaser"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[560px]">
          <div
            className="relative bg-cover bg-center min-h-[420px] lg:min-h-full"
            style={{ backgroundImage: `url(${resolveAssetUrl(BRAND.LADY_STARZZ_TEAM)})` }}
            aria-hidden="true"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0b0b0b]" />
          </div>
          <div className="flex flex-col justify-center px-8 sm:px-16 py-16 lg:py-24">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={resolveAssetUrl(BRAND.WSBL_LOGO)}
                alt="WSBL"
                className="h-10 w-10 object-contain"
              />
              <span className="text-[10px] tracking-[0.35em] uppercase text-zinc-400 font-bold">
                WSBL · 2027 Inaugural Season
              </span>
            </div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-2">
              // Faith Athletic Women's Team
            </div>
            <h2 className="font-heading text-4xl sm:text-6xl font-black uppercase leading-[0.95] tracking-tight">
              Texas Lady
              <br />
              <span className="metallic-text">Starzz</span>
            </h2>
            <p className="text-zinc-300 text-base leading-relaxed mt-6 max-w-lg">
              Houston-based · Pro-ready talent · Faith first. Competing in the Women's Southwest Basketball League beginning 2027 — built to share Christ on every floor we step on.
            </p>
            <p className="text-zinc-500 text-sm mt-4 max-w-lg italic">
              Calling all women's basketball athletes who still have the fire. If your faith has shaped your story, we want your film.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/lady-starzz"
                className="bg-white text-black font-heading font-bold text-sm tracking-[0.25em] uppercase px-7 py-4 hover:bg-zinc-200 transition-colors inline-flex items-center gap-2"
                data-testid="lady-starzz-cta"
              >
                Meet the Team
              </Link>
              <Link
                to="/lady-starzz#apply"
                className="border border-zinc-700 text-white font-heading font-bold text-sm tracking-[0.25em] uppercase px-7 py-4 hover:border-white transition-colors inline-flex items-center gap-2"
                data-testid="lady-starzz-apply-cta"
              >
                Apply for Tryout
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CULTURE */}
      <section
        id="culture"
        className="cultural-bg relative py-24 border-y border-zinc-800 my-12 scroll-mt-24"
        style={{ backgroundImage: `url(${resolveAssetUrl(BRAND.FAMILY)})` }}
        data-testid="culture-section"
      >
        <div className="relative z-10 max-w-5xl mx-auto text-center px-6 space-y-8">
          <div className="flex justify-center items-center space-x-2">
            <span className="text-xs font-bold tracking-[0.4em] uppercase text-zinc-400">
              FAITH • FAMILY • CULTURE • LEGACY
            </span>
          </div>

          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight uppercase italic">
            “Fa’aaloalo. Fa’afetai. Fa’asoa.”
          </h2>

          <p className="text-zinc-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed font-light">
            Deeply rooted in ancient strength, respect, and values. Our garments
            carry the spiritual warrior essence—built to endure the heaviest
            weights and celebrate community roots.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 text-left max-w-4xl mx-auto">
            <div className="border border-zinc-800 p-6 bg-black/50 backdrop-blur">
              <h3 className="font-heading text-xl font-bold uppercase mb-2 text-white">
                Fa'aaloalo
              </h3>
              <p className="text-xs text-zinc-400">
                Respect for your temple, your family line, and the discipline
                it takes to step onto the field.
              </p>
            </div>
            <div className="border border-zinc-800 p-6 bg-black/50 backdrop-blur">
              <h3 className="font-heading text-xl font-bold uppercase mb-2 text-white">
                Fa'afetai
              </h3>
              <p className="text-xs text-zinc-400">
                Gratitude for the struggles that forged your grit, power, and
                ultimate competitive drive.
              </p>
            </div>
            <div className="border border-zinc-800 p-6 bg-black/50 backdrop-blur">
              <h3 className="font-heading text-xl font-bold uppercase mb-2 text-white">
                Fa'asoa
              </h3>
              <p className="text-xs text-zinc-400">
                Sharing your blessings, lifting up your community, and passing
                the legacy to the youth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section
        id="about"
        className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center scroll-mt-24"
        data-testid="about-section"
      >
        <div className="space-y-6">
          <h3 className="text-xs font-bold tracking-[0.3em] text-zinc-500 uppercase">
            // OUR MISSION STATEMENT
          </h3>
          <h2 className="font-heading text-5xl font-black uppercase tracking-tight leading-none">
            I CAN DO ALL THINGS <br />
            <span className="metallic-text">THROUGH CHRIST</span> <br />
            WHO STRENGTHENS ME.
          </h2>
          <p className="text-sm text-zinc-400 tracking-wide leading-relaxed">
            Faith Athletic Sportswear isn't just about raw muscle; it's about
            spiritual purpose matching physical action. We create
            high-performance gear featuring premium tactical layouts, deep
            support construction, and symbolic silver design highlights to
            match your relentless grind.
          </p>
          <blockquote className="border-l-4 border-zinc-500 pl-4 italic text-zinc-300 text-xs tracking-wider uppercase">
            "As for me and my house, we will serve the Lord." — Joshua 24:15
          </blockquote>
        </div>

        <div
          className="relative bg-zinc-900 border border-zinc-800 p-8 flex flex-col justify-between min-h-[450px] overflow-hidden group"
        >
          <div
            className="absolute inset-0 about-card-bg opacity-25 group-hover:scale-105 transition-transform duration-700"
            style={{ backgroundImage: `url(${resolveAssetUrl(BRAND.STRENGTH_DUO)})` }}
          ></div>
          <div className="relative z-10">
            <span className="font-heading text-6xl font-black opacity-10 block tracking-tighter">
              FAITH
            </span>
          </div>
          <div className="relative z-10 space-y-4">
            <span className="text-xs font-bold bg-white text-black px-3 py-1 uppercase tracking-widest inline-block">
              Built to Overcome
            </span>
            <h3 className="font-heading text-3xl font-bold uppercase tracking-tight">
              Heavy Duty Training Gear Available Now
            </h3>
            <p className="text-xs text-zinc-400 max-w-sm">
              Engineered with double-reinforced stitching and sweat-wicking
              multi-blend textiles to survive maximal effort sets.
            </p>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section
        className="bg-zinc-950 border-t border-zinc-900 py-20"
        data-testid="newsletter-section"
      >
        <div className="max-w-3xl mx-auto text-center px-6 space-y-6">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight">
            Join the Faith Athletic Circle
          </h2>
          <p className="text-zinc-400 text-xs tracking-widest uppercase">
            Sign up to receive priority restock alerts, collection drops, and
            exclusive community content.
          </p>
          <form
            onSubmit={handleNewsletter}
            className="flex flex-col sm:flex-row gap-4 pt-4 max-w-md mx-auto"
            data-testid="newsletter-form"
          >
            <input
              type="email"
              placeholder="ENTER YOUR EMAIL"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-zinc-900 border border-zinc-800 text-white text-xs font-bold uppercase tracking-wider px-4 py-3 outline-none focus:border-zinc-500 transition-colors"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="bg-white text-black font-heading font-bold text-xs tracking-widest uppercase px-6 py-3 hover:bg-zinc-300 transition-colors disabled:opacity-50"
              data-testid="newsletter-submit-btn"
            >
              {submitting ? "..." : "Subscribe"}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
